import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import AdmissionTaskChecklist from './AdmissionTaskChecklist';
import { WORKFLOW_KEYS } from '../../data/workflowChecklistTemplates';
import { Tooltip } from '../Common/ReusableUI';
import { useToast } from '../Common/ToastProvider';
import { completionPercent } from '../../services/workflow/TaskService';
import { ensureWorkflow } from '../../services/workflow/WorkflowEngine';
import {
  TrendingUp,
  CheckCircle2,
  Circle,
  ChevronRight,
  BookOpen,
  Sparkles,
  Inbox,
  UserCheck,
  MapPin,
  Timer,
  Handshake,
  CalendarCheck2,
  Home,
  DoorOpen,
  LineChart,
  History,
  Shield,
  Sunrise,
  LayoutDashboard,
  AlertTriangle,
  BedDouble,
  HeartPulse,
  Printer,
  FileDown,
  ArrowRight,
  CalendarDays,
  Lock
} from 'lucide-react';
import { getJourneyStageFormModel } from './crmJourneyStageData';
import { getJourneyPosition, saveJourneyPosition } from '../../utils/residentJourneyStorage';

/** Stage 7 substage → operational workflow template (Phase 1) */
const SUBSTAGE_WORKFLOW_KEY = {
  '7A': WORKFLOW_KEYS.STAGE7A_SETTLING,
  '7B': WORKFLOW_KEYS.STAGE7B_STEADY,
  '7C': WORKFLOW_KEYS.STAGE7C_ESCALATED,
  '7D': WORKFLOW_KEYS.STAGE7D_RESPITE,
  '7E': WORKFLOW_KEYS.STAGE7E_PALLIATIVE,
};

const SUBSTAGE_CHECKLIST_TITLE = {
  '7A': 'Stage 7A — Settling in (0–28 days)',
  '7B': 'Stage 7B — Steady state (ongoing)',
  '7C': 'Stage 7C — Escalated / complex care',
  '7D': 'Stage 7D — Respite management',
  '7E': 'Stage 7E — Palliative / end of life',
};

/** CRM stages 1–4 — pre-admission pipeline checklists */
const PIPELINE_STAGE_WORKFLOW = {
  1: WORKFLOW_KEYS.STAGE1_ENQUIRY,
  2: WORKFLOW_KEYS.STAGE2_QUALIFIED,
  3: WORKFLOW_KEYS.STAGE3_TOUR,
  4: WORKFLOW_KEYS.STAGE4_WAITLIST,
};

const PIPELINE_CHECKLIST_TITLE = {
  1: 'Stage 1 — Enquiry (operational checklist)',
  2: 'Stage 2 — Qualified lead (operational checklist)',
  3: 'Stage 3 — Tour & assessment (operational checklist)',
  4: 'Stage 4 — Waitlist (operational checklist)',
};

/** Full stage→workflow mapping for progress tracking */
const STAGE_WORKFLOW_KEY_MAP = {
  1: WORKFLOW_KEYS.STAGE1_ENQUIRY,
  2: WORKFLOW_KEYS.STAGE2_QUALIFIED,
  3: WORKFLOW_KEYS.STAGE3_TOUR,
  4: WORKFLOW_KEYS.STAGE4_WAITLIST,
  5: WORKFLOW_KEYS.STAGE5_OFFER,
  6: WORKFLOW_KEYS.STAGE6_ADMISSION,
  8: WORKFLOW_KEYS.STAGE8_EXIT,
};

/** What each stage involves (shown as tooltip) */
const STAGE_TOOLTIP_MAP = {
  1: 'Capture inbound contact, acknowledge within 2 hours, assign coordinator',
  2: 'Verify ACAT approval, means testing, clinical suitability screening',
  3: 'Schedule and conduct facility tour, pre-admission clinical assessment',
  4: 'Place on waitlist, set priority band, maintain regular contact',
  5: 'Present residential agreement, explain fees, obtain signatures',
  6: 'Move-in day coordination, room setup, initial assessments',
  7: 'Active resident care — settling in through to palliative',
  8: 'Departure planning, final reconciliation, handover documentation',
  9: 'Post-exit follow-up, family feedback, bed turnover'
};

/**
 * Anchor for stages 1–4 due dates.
 */
function derivePipelineAnchorIso(admissionDateIso, enquiryDateIso, residentId) {
  if (enquiryDateIso && /^\d{4}-\d{2}-\d{2}/.test(String(enquiryDateIso))) {
    return String(enquiryDateIso).split('T')[0];
  }
  if (admissionDateIso && /^\d{4}-\d{2}-\d{2}/.test(String(admissionDateIso))) {
    const d = new Date(String(admissionDateIso).split('T')[0]);
    const rid = Number(residentId) || 1;
    const offsetDays = 55 + (rid % 45);
    d.setDate(d.getDate() - offsetDays);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return new Date().toISOString().split('T')[0];
}

const SUBSTAGE_LABEL = {
  '7A': 'Transitions in',
  '7B': 'Steady state',
  '7C': 'High risk / Sentinel',
  '7D': 'Hospital transition',
  '7E': 'End of life',
};

/** Primary journey tiles — refined icons, colour accents per phase */
const STAGE_NAV = [
  { stage: 1, name: 'Enquiry',   short: 'Inbound lead captured',   Icon: Inbox,         journeyStatus: 'completed', accent: 'emerald' },
  { stage: 2, name: 'Qualified', short: 'ACAT verified',           Icon: UserCheck,     journeyStatus: 'completed', accent: 'emerald' },
  { stage: 3, name: 'Tour',      short: 'Facility visit',          Icon: MapPin,        journeyStatus: 'completed', accent: 'emerald' },
  { stage: 4, name: 'Waitlist',  short: 'Queue position set',      Icon: Timer,         journeyStatus: 'completed', accent: 'emerald' },
  { stage: 5, name: 'Offer',     short: 'Agreement & fees',        Icon: Handshake,     journeyStatus: 'completed', accent: 'emerald' },
  { stage: 6, name: 'Admission', short: 'Move-in day',             Icon: CalendarCheck2,journeyStatus: 'completed', accent: 'emerald' },
  { stage: 7, name: 'Active',    short: 'Resident care',           Icon: Home,          journeyStatus: 'current',   accent: 'indigo'  },
  { stage: 8, name: 'Exit',      short: 'Departure',               Icon: DoorOpen,      journeyStatus: 'pending',   accent: 'slate'   },
  { stage: 9, name: 'Post-exit', short: 'Follow-up',               Icon: LineChart,     journeyStatus: 'pending',   accent: 'slate'   },
];

/** Average stage duration in days (for estimated timeline) */
const STAGE_AVG_DAYS = { 1: 3, 2: 5, 3: 7, 4: 10, 5: 4, 6: 1, 7: 0, 8: 3, 9: 5 };

/** Substage 7A–7E — icon + colour per substage */
const SUBSTAGE_CONFIG = {
  '7A': { Icon: Sunrise,         gradient: 'from-amber-500 to-orange-500',  ring: 'ring-amber-200',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-300',  activeBg: 'bg-amber-500' },
  '7B': { Icon: LayoutDashboard, gradient: 'from-emerald-500 to-teal-500', ring: 'ring-emerald-200',bg: 'bg-emerald-50',text: 'text-emerald-700',border: 'border-emerald-300',activeBg: 'bg-emerald-500' },
  '7C': { Icon: AlertTriangle,   gradient: 'from-red-500 to-rose-500',     ring: 'ring-red-200',    bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-300',    activeBg: 'bg-red-500' },
  '7D': { Icon: BedDouble,       gradient: 'from-blue-500 to-cyan-500',    ring: 'ring-blue-200',   bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-300',   activeBg: 'bg-blue-500' },
  '7E': { Icon: HeartPulse,      gradient: 'from-purple-500 to-fuchsia-500',ring:'ring-purple-200', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300', activeBg: 'bg-purple-500' },
};

function JourneyHistoryTable({ history }) {
  if (!history) return null;
  const { title, subtitle, columns, rows } = history;
  return (
    <div className="border-t border-slate-100 pt-8">
      <div className="mb-4">
        <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
          <History className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
          {title}
        </h4>
        {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-inner">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
              {columns.map((c) => (
                <th key={c.key} className="whitespace-nowrap px-3 py-3">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rows || rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-slate-500"
                >
                  No events recorded yet.
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-slate-100 last:border-0 odd:bg-white even:bg-slate-50/50"
                >
                  {columns.map((c) => (
                    <td key={c.key} className="max-w-[min(280px,40vw)] px-3 py-2.5 text-slate-800">
                      <span className="line-clamp-3">{row[c.key] ?? '—'}</span>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Resident-level compliance / quality indicators — intended for the Profile tab
 * so staff see priorities before drilling into CRM journey stages.
 */
export function ComplianceSnapshotCard({
  sirsCount = 0,
  qualityIncidentCount = 0,
  careMinutesCompliant = true,
  starRating = '4.2',
  onOpenCrm
}) {
  const nqipDisplay =
    qualityIncidentCount === 0 ? '100%' : `${Math.max(0, 100 - qualityIncidentCount * 5)}%`;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[ComplianceSnapshotCard] render', {
        sirsCount,
        qualityIncidentCount,
        careMinutesCompliant,
        nqipDisplay
      });
    }
  }, [sirsCount, qualityIncidentCount, careMinutesCompliant, nqipDisplay]);

  return (
    <div className="mf-card overflow-hidden shadow-sm border-slate-200">
      <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Shield className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden />
            Compliance priority snapshot
          </h3>
          <p className="text-xs text-slate-500">
            Resident-level quality indicators — use this to spot what needs attention first.
          </p>
        </div>
        {typeof onOpenCrm === 'function' ? (
          <button
            type="button"
            onClick={onOpenCrm}
            className="touch-target inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50"
          >
            CRM journey
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4">
        <Tooltip content="National Quality Indicator Program — percentage alignment across all quality domains for this resident" position="bottom">
          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 text-center cursor-help">
            <div className="text-2xl font-bold text-emerald-600">{nqipDisplay}</div>
            <div className="text-xs font-medium text-slate-600">NQIP alignment</div>
          </div>
        </Tooltip>
        <Tooltip content="Serious Incident Response Scheme — reportable incidents involving this resident that require notification to the Aged Care Quality and Safety Commission" position="bottom">
          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 text-center cursor-help">
            <div className="text-2xl font-bold text-blue-600">{sirsCount}</div>
            <div className="text-xs font-medium text-slate-600">SIRS (this resident)</div>
          </div>
        </Tooltip>
        <Tooltip content="Facility-wide Star Rating from the Aged Care Quality and Safety Commission — 1 to 5 stars" position="bottom">
          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 text-center cursor-help">
            <div className="text-2xl font-bold text-violet-600">{starRating}★</div>
            <div className="text-xs font-medium text-slate-600">Star (facility)</div>
          </div>
        </Tooltip>
        <Tooltip content="Whether today's allocated care minutes meet the AN-ACC funded minutes target for this resident" position="bottom">
          <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 text-center cursor-help">
            <div
              className={`text-2xl font-bold ${careMinutesCompliant ? 'text-amber-600' : 'text-red-600'}`}
            >
              {careMinutesCompliant ? 'OK' : 'Review'}
            </div>
            <div className="text-xs font-medium text-slate-600">Care minutes (today)</div>
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

function FormField({ label, value, hint, fullWidth }) {
  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
        {label}
      </label>
      <div
        className="min-h-[42px] w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-900 shadow-inner"
        role="group"
        aria-label={label}
      >
        {value || '—'}
      </div>
      {hint ? (
        <p className="mt-1 text-[11px] text-slate-400 leading-snug">{hint}</p>
      ) : null}
    </div>
  );
}

/** Mini progress bar for stage tiles */
function MiniProgress({ percent, completed, total }) {
  const barColor = percent >= 100 ? 'bg-emerald-500' : percent > 50 ? 'bg-indigo-500' : percent > 0 ? 'bg-amber-500' : 'bg-slate-300';
  return (
    <div className="w-full mt-1">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[9px] font-bold text-slate-500">{completed}/{total}</span>
        <span className="text-[9px] font-bold text-slate-500">{percent}%</span>
      </div>
      <div className="h-1 w-full rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/** Compute estimated dates for each stage based on admission anchor */
function computeStageDates(admissionDateIso, residentId) {
  const dates = {};
  const rid = Number(residentId) || 1;
  const anchorMs = new Date(admissionDateIso).getTime();
  let cumulativeDaysBefore = 0;
  for (let s = 6; s >= 1; s--) {
    cumulativeDaysBefore += STAGE_AVG_DAYS[s] || 4;
    const stageDate = new Date(anchorMs - cumulativeDaysBefore * 86400000);
    dates[s] = stageDate.toISOString().split('T')[0];
  }
  dates[6] = admissionDateIso;
  dates[7] = admissionDateIso;
  let cumulativeDaysAfter = 0;
  for (let s = 8; s <= 9; s++) {
    cumulativeDaysAfter += STAGE_AVG_DAYS[s] || 4;
    const stageDate = new Date(anchorMs + cumulativeDaysAfter * 86400000);
    dates[s] = stageDate.toISOString().split('T')[0];
  }
  return dates;
}

function formatShortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

const CrmComplianceProfileSection = ({
  residentId,
  residentName,
  facilityId: facilityIdProp,
  admissionDateIso: admissionDateProp,
  enquiryDateIso: enquiryDateProp,
}) => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const facilityId = facilityIdProp || state.selectedFacility || 'sunshine';
  const admissionDateIso =
    admissionDateProp ||
    new Date().toISOString().split('T')[0];

  const pipelineAnchorIso = useMemo(
    () => derivePipelineAnchorIso(admissionDateProp, enquiryDateProp, residentId),
    [admissionDateProp, enquiryDateProp, residentId]
  );

  const [stageTab, setStageTab] = useState(7);
  const [subStage7, setSubStage7] = useState('7B');
  const [stageProgress, setStageProgress] = useState({});
  const [advanceWarning, setAdvanceWarning] = useState(null);

  useEffect(() => {
    if (residentId == null) return;
    const p = getJourneyPosition(residentId);
    setStageTab(p.stageTab);
    setSubStage7(p.subStage7);
  }, [residentId]);

  // Compute task completion for every stage that has a workflow
  const refreshProgress = useCallback(() => {
    if (residentId == null) return;
    const progress = {};
    for (const [stage, wfKey] of Object.entries(STAGE_WORKFLOW_KEY_MAP)) {
      try {
        ensureWorkflow(residentId, facilityId, admissionDateIso, wfKey);
        progress[stage] = completionPercent(residentId, wfKey);
      } catch {
        progress[stage] = { percent: 0, completed: 0, total: 0 };
      }
    }
    // Stage 7 substages
    for (const [sub, wfKey] of Object.entries(SUBSTAGE_WORKFLOW_KEY)) {
      try {
        ensureWorkflow(residentId, facilityId, admissionDateIso, wfKey);
        progress[`7_${sub}`] = completionPercent(residentId, wfKey);
      } catch {
        progress[`7_${sub}`] = { percent: 0, completed: 0, total: 0 };
      }
    }
    setStageProgress(progress);
  }, [residentId, facilityId, admissionDateIso]);

  useEffect(() => {
    refreshProgress();
    const interval = setInterval(refreshProgress, 3000);
    return () => clearInterval(interval);
  }, [refreshProgress]);

  const persistJourney = (nextStage, nextSub) => {
    if (residentId == null) return;
    saveJourneyPosition(residentId, {
      stageTab: nextStage,
      subStage7: nextStage === 7 ? nextSub : '7B',
    });
  };

  const model = useMemo(
    () =>
      getJourneyStageFormModel(
        stageTab,
        residentName,
        residentId,
        stageTab === 7 ? subStage7 : '7B'
      ),
    [stageTab, residentName, residentId, subStage7]
  );

  const stageDates = useMemo(
    () => computeStageDates(admissionDateIso, residentId),
    [admissionDateIso, residentId]
  );

  const handleAdvanceStage = useCallback(() => {
    const currentStageIdx = STAGE_NAV.findIndex(s => s.stage === stageTab);
    if (currentStageIdx < 0 || currentStageIdx >= STAGE_NAV.length - 1) return;

    const progressKey = stageTab === 7 ? `7_${subStage7}` : String(stageTab);
    const prog = stageProgress[progressKey];

    if (prog && prog.total > 0 && prog.completed < prog.total) {
      const incomplete = prog.total - prog.completed;
      setAdvanceWarning(`${incomplete} task${incomplete > 1 ? 's' : ''} still incomplete in this stage. Complete all tasks before advancing.`);
      return;
    }

    const nextStage = STAGE_NAV[currentStageIdx + 1].stage;
    setStageTab(nextStage);
    persistJourney(nextStage, subStage7);
    setAdvanceWarning(null);
    showToast(`Advanced to Stage ${nextStage} — ${STAGE_NAV[currentStageIdx + 1].name}`, 'success');
  }, [stageTab, subStage7, stageProgress, showToast]);

  const canAdvance = useMemo(() => {
    const progressKey = stageTab === 7 ? `7_${subStage7}` : String(stageTab);
    const prog = stageProgress[progressKey];
    if (!prog || prog.total === 0) return true;
    return prog.completed >= prog.total;
  }, [stageTab, subStage7, stageProgress]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[CrmComplianceProfileSection] stage model', {
        stageTab,
        subStage7: stageTab === 7 ? subStage7 : null,
        residentId,
        sectionCount: model?.sections?.length,
        hasHistory: Boolean(model?.history),
        stageProgress
      });
    }
  }, [stageTab, subStage7, residentId, model, stageProgress]);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-700 via-indigo-600 to-blue-700 p-6 sm:p-8 text-white shadow-xl">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-cyan-400/20 blur-xl" />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" />
              Annex C — CRM pipeline
            </div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl flex items-center gap-2">
              <TrendingUp className="h-8 w-8 shrink-0" />
              Resident journey & CRM record
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-indigo-100 leading-relaxed">
              Select a <strong>stage tab</strong> to view spec-aligned fields (read-only demo). Stage 7
              includes <strong>sub-tabs</strong> for active-resident substages (7A–7E).
            </p>
          </div>
          <div className="flex items-start gap-2">
            <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm backdrop-blur">
              <div className="text-indigo-100">Current pipeline position</div>
              <div className="text-2xl font-bold">
                Stage {stageTab} of 9
              </div>
              <div className="text-xs text-indigo-200">
                {STAGE_NAV.find((s) => s.stage === stageTab)?.name || 'Stage'} resident
                {stageTab === 7 ? ` — ${subStage7} ${SUBSTAGE_LABEL[subStage7] || ''}` : ''}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20 transition-colors backdrop-blur"
              >
                <Printer className="h-3.5 w-3.5" />
                Print
              </button>
              <button
                type="button"
                onClick={() => { window.print(); showToast('PDF export initiated via browser print', 'info'); }}
                className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20 transition-colors backdrop-blur"
              >
                <FileDown className="h-3.5 w-3.5" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Journey stages — connected timeline ─────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-3 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
            Resident journey — 9-stage pipeline
          </p>
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <CalendarDays className="h-3 w-3" />
            Estimated timeline based on avg. stage durations
          </div>
        </div>

        {/* Timeline rail + tiles */}
        <div className="relative px-4 pt-6 pb-5 overflow-x-auto scrollbar-thin">
          {/* Connecting rail behind the tiles */}
          <div className="pointer-events-none absolute left-0 right-0 top-[3.65rem] flex items-center px-4" aria-hidden>
            <div className="h-[3px] w-full rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-indigo-500 transition-all duration-700"
                style={{ width: `${((STAGE_NAV.filter(s => s.journeyStatus === 'completed').length) / STAGE_NAV.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="relative flex gap-1 sm:gap-0">
            {STAGE_NAV.map((s) => {
              const active = stageTab === s.stage;
              const done = s.journeyStatus === 'completed';
              const current = s.journeyStatus === 'current';
              const StageIcon = s.Icon;

              const progressKey = String(s.stage);
              const prog = stageProgress[progressKey];
              const hasProgress = prog && prog.total > 0;

              const iconContainerClass = done
                ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-200/60 ring-4 ring-emerald-100'
                : current
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/60 ring-4 ring-indigo-100 animate-pulse'
                  : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 ring-4 ring-slate-50';

              const tileClass = active
                ? 'border-indigo-300 bg-gradient-to-b from-indigo-50/80 via-white to-white shadow-xl shadow-indigo-100/50 ring-1 ring-indigo-200/60 scale-[1.04]'
                : 'border-transparent bg-transparent hover:bg-slate-50/80 hover:border-slate-200';

              return (
                <button
                  key={s.stage}
                  type="button"
                  onClick={() => {
                    setStageTab(s.stage);
                    persistJourney(s.stage, subStage7);
                    setAdvanceWarning(null);
                  }}
                  title={STAGE_TOOLTIP_MAP[s.stage] || s.short}
                  className={`group relative flex shrink-0 flex-col items-center gap-1 rounded-2xl border px-3 py-3 text-center transition-all duration-300 ease-out min-w-[6.5rem] sm:min-w-[7rem] md:flex-1 ${tileClass}`}
                >
                    {/* Step number badge */}
                    <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-bold shadow-sm ${
                      done ? 'bg-emerald-500 text-white' : current ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {done ? '✓' : s.stage}
                    </span>

                    {/* Task count badge */}
                    {hasProgress && (
                      <span className={`absolute -top-2.5 right-1 flex items-center justify-center rounded-full px-1.5 py-0.5 text-[9px] font-bold shadow-sm ${
                        prog.percent >= 100
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {prog.completed}/{prog.total}
                      </span>
                    )}

                    {/* Icon container */}
                    <span className={`relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 ${iconContainerClass}`} style={{ zoom: 1 }}>
                      <StageIcon
                        className="h-5 w-5 sm:h-[1.35rem] sm:w-[1.35rem]"
                        strokeWidth={done || current ? 2.25 : 1.5}
                        aria-hidden
                        style={{ zoom: 1 }}
                      />
                      {current && (
                        <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-indigo-500 ring-2 ring-white" />
                        </span>
                      )}
                    </span>

                    {/* Label */}
                    <span className={`mt-1 text-xs font-bold tracking-wide leading-tight ${
                      active ? 'text-indigo-900' : done ? 'text-emerald-800' : current ? 'text-indigo-700' : 'text-slate-500'
                    }`}>
                      {s.name}
                    </span>

                    {/* Description */}
                    <span className={`text-[10px] leading-tight ${
                      active ? 'text-indigo-600/80' : 'text-slate-400'
                    }`}>
                      {s.short}
                    </span>

                    {/* Estimated date */}
                    <span className={`text-[9px] leading-tight font-medium ${
                      done ? 'text-emerald-600' : current ? 'text-indigo-500' : 'text-slate-400'
                    }`}>
                      {done ? `✓ ${formatShortDate(stageDates[s.stage])}` : current ? 'In progress' : `Est. ${formatShortDate(stageDates[s.stage])}`}
                    </span>

                    {/* Progress bar */}
                    {hasProgress && (
                      <div className="w-full px-1">
                        <MiniProgress percent={prog.percent} completed={prog.completed} total={prog.total} />
                      </div>
                    )}

                    {/* Active indicator bar */}
                    {active && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                    )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stage transition controls */}
        <div className="border-t border-slate-100 px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            Current: <strong>Stage {stageTab} — {STAGE_NAV.find(s => s.stage === stageTab)?.name}</strong>
            {stageTab < 9 && (
              <span className="ml-2">
                → Next: Stage {stageTab + 1} — {STAGE_NAV.find(s => s.stage === stageTab + 1)?.name}
              </span>
            )}
          </div>
          {stageTab < 9 && (
            <Tooltip
              content={canAdvance ? `Advance to Stage ${stageTab + 1}` : 'Complete all tasks before advancing'}
              position="left"
            >
              <button
                type="button"
                onClick={handleAdvanceStage}
                disabled={!canAdvance}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold shadow-sm transition-colors ${
                  canAdvance
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {canAdvance ? (
                  <>
                    <ArrowRight className="h-3.5 w-3.5" />
                    Advance to Next Stage
                  </>
                ) : (
                  <>
                    <Lock className="h-3.5 w-3.5" />
                    Complete Tasks to Advance
                  </>
                )}
              </button>
            </Tooltip>
          )}
        </div>

        {/* Advance warning */}
        {advanceWarning && (
          <div className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-800">{advanceWarning}</p>
              <button
                type="button"
                onClick={() => setAdvanceWarning(null)}
                className="mt-1 text-[10px] text-amber-600 hover:text-amber-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* ── Stage 7 substage cards ──────────────────────────────── */}
        {stageTab === 7 && model.subStageTabs && (
          <div className="border-t border-slate-100">
            <div className="bg-gradient-to-r from-violet-50/60 via-purple-50/40 to-indigo-50/30 px-5 py-4">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-violet-600">
                Active resident — substages
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {model.subStageTabs.map((t) => {
                  const on = subStage7 === t.id;
                  const cfg = SUBSTAGE_CONFIG[t.id] || SUBSTAGE_CONFIG['7B'];
                  const SubIcon = cfg.Icon;
                  const titleLine = t.label.replace(/^\S+\s/, '');

                  const subProg = stageProgress[`7_${t.id}`];
                  const hasSubProgress = subProg && subProg.total > 0;

                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSubStage7(t.id);
                        persistJourney(7, t.id);
                        setAdvanceWarning(null);
                        showToast(`Viewing substage ${t.id} — ${titleLine}`, 'info');
                      }}
                      className={`group relative flex flex-col items-center gap-2 rounded-2xl border-2 px-3 py-4 text-center transition-all duration-300 ${
                        on
                          ? `${cfg.border} bg-white shadow-lg ring-1 ${cfg.ring} scale-[1.03]`
                          : 'border-slate-200/60 bg-white/80 hover:border-slate-300 hover:bg-white hover:shadow-md'
                      }`}
                    >
                      {/* Coloured icon circle */}
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110 ${
                        on
                          ? `bg-gradient-to-br ${cfg.gradient} text-white shadow-md`
                          : `${cfg.bg} ${cfg.text}`
                      }`}>
                        <SubIcon className="h-5 w-5" strokeWidth={on ? 2.25 : 1.75} aria-hidden />
                      </span>

                      {/* Substage code + task badge */}
                      <div className="flex items-center gap-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-widest ${
                          on ? `${cfg.activeBg} text-white` : 'bg-slate-100 text-slate-500'
                        }`}>
                          {t.id}
                        </span>
                        {hasSubProgress && (
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                            subProg.percent >= 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {subProg.completed}/{subProg.total}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <span className={`text-xs font-semibold leading-tight ${
                        on ? 'text-slate-900' : 'text-slate-600'
                      }`}>
                        {titleLine}
                      </span>

                      {/* Short description */}
                      <span className="text-[10px] leading-snug text-slate-400 line-clamp-2">
                        {t.short}
                      </span>

                      {/* Progress bar for substage */}
                      {hasSubProgress && (
                        <div className="w-full px-1">
                          <MiniProgress percent={subProg.percent} completed={subProg.completed} total={subProg.total} />
                        </div>
                      )}

                      {/* Active dot */}
                      {on && (
                        <span className={`absolute -top-1.5 right-2 h-3 w-3 rounded-full ${cfg.activeBg} ring-2 ring-white shadow-sm`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {[1, 2, 3, 4].includes(stageTab) && PIPELINE_STAGE_WORKFLOW[stageTab] && residentId != null && (
        <AdmissionTaskChecklist
          residentId={residentId}
          facilityId={facilityId}
          admissionDateIso={admissionDateIso}
          workflowAnchorIso={pipelineAnchorIso}
          workflowKey={PIPELINE_STAGE_WORKFLOW[stageTab]}
          title={PIPELINE_CHECKLIST_TITLE[stageTab]}
          currentUserId={state.currentUser?.id || state.currentUser?.username || 'user'}
        />
      )}

      {stageTab === 6 && residentId != null && (
        <AdmissionTaskChecklist
          residentId={residentId}
          facilityId={facilityId}
          admissionDateIso={admissionDateIso}
          workflowKey={WORKFLOW_KEYS.STAGE6_ADMISSION}
          title="Stage 6 — Admission in progress (operational checklist)"
          currentUserId={state.currentUser?.id || state.currentUser?.username || 'user'}
        />
      )}
      {stageTab === 7 && SUBSTAGE_WORKFLOW_KEY[subStage7] && residentId != null && (
        <AdmissionTaskChecklist
          residentId={residentId}
          facilityId={facilityId}
          admissionDateIso={admissionDateIso}
          workflowKey={SUBSTAGE_WORKFLOW_KEY[subStage7]}
          title={SUBSTAGE_CHECKLIST_TITLE[subStage7] || 'Operational tasks'}
          currentUserId={state.currentUser?.id || state.currentUser?.username || 'user'}
        />
      )}

      {/* Form card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
        <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">{model.title}</h3>
              {model.subStageTitle && (
                <p className="mt-1 text-sm font-semibold text-indigo-700">{model.subStageTitle}</p>
              )}
              <p className="mt-1 text-sm text-slate-600">{model.subtitle || model.subStageDescription}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                  model.status === 'completed'
                    ? 'bg-emerald-100 text-emerald-800'
                    : model.status === 'current'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-slate-100 text-slate-600'
                }`}
              >
                {model.status === 'completed'
                  ? 'Completed'
                  : model.status === 'current'
                    ? 'Current'
                    : 'Pending'}
              </span>
              {model.specRef && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900 border border-amber-200">
                  <BookOpen className="h-3 w-3" />
                  {model.specRef}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-8 p-5 sm:p-6">
          {model.sections.map((section) => (
            <section key={section.title}>
              <h4 className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-2 text-sm font-bold uppercase tracking-wide text-slate-700">
                {section.title}
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {section.fields.map((f) => (
                  <FormField
                    key={f.key}
                    label={f.label}
                    value={f.value}
                    hint={f.hint}
                    fullWidth={f.fullWidth}
                  />
                ))}
              </div>
            </section>
          ))}

          <JourneyHistoryTable history={model.history} />
        </div>
      </div>
    </div>
  );
};

export default CrmComplianceProfileSection;
